import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "@web/theme/appTheme";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import {
  ArrowRight,
  ChartNoAxesColumnIncreasing,
  Eye,
  EyeOff,
  House,
  LockKeyhole,
  UserRound,
  UsersRound,
} from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { ApiError } from "@lib/api/api-types";
import { useAuth } from "@features/auth/context/AuthContext";

import "./login-page.css";

export const loginSchema = z.object({
  email: z.string().trim().min(1, "Enter your username or email"),
  password: z.string().min(1, "Enter your password"),
  rememberMe: z.boolean(),
});
type FormValues = z.infer<typeof loginSchema>;

const loginTheme = createAppTheme("dark");

export function LoginPage() {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState("");
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "", rememberMe: true },
  });
  const submit = async (values: FormValues) => {
    setApiError("");
    try {
      await login(values);
    } catch (error) {
      setApiError(
        error instanceof ApiError || error instanceof Error
          ? error.message
          : "Unable to log in.",
      );
    }
  };
  return (
    <ThemeProvider theme={loginTheme}>
      <main className="orbit-login">
        <header className="orbit-login__header">
          <div
            className="orbit-login__brand"
            aria-label="ORBIT — A smarter way to real estate"
          >
            <img
              className="orbit-login__logo"
              src="/branding/orbit-logo-white.png"
              alt="ORBIT"
              width={1600}
              height={628}
            />
            <span className="orbit-login__tagline">
              One Platform. Infinite Possibilities.
            </span>
          </div>
          <p className="orbit-login__nav">
            Listings <span>•</span> Leads <span>•</span> Relationships{" "}
            <span>•</span> Growth <i />
          </p>
        </header>

        <div className="orbit-login__content">
          <section className="orbit-login__card" aria-labelledby="login-title">
            <div className="orbit-login__watermark" aria-hidden="true">
              <img src="/branding/orbit-emblem-white.png" alt="" />
            </div>
            <p className="orbit-login__eyebrow">Welcome back</p>
            <h1 id="login-title">Log in to ORBIT</h1>
            <p className="orbit-login__intro">
              Access property listings, manage leads and follow-ups in the
              secure workspace.
            </p>
            <Stack
              component="form"
              onSubmit={(event) => void handleSubmit(submit)(event)}
              spacing={2.5}
              noValidate
            >
              {searchParams.get("reason") === "session-expired" && (
                <Alert severity="info">
                  Your session has expired. Please log in again to continue.
                </Alert>
              )}
              {apiError && <Alert severity="error">{apiError}</Alert>}
              <div className="orbit-login__field">
                <label htmlFor="orbit-login-identity">Username</label>
                <TextField
                  id="orbit-login-identity"
                  placeholder="Enter your username"
                  autoComplete="username"
                  fullWidth
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <UserRound size={21} />
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...register("email")}
                />
              </div>
              <div className="orbit-login__field">
                <label htmlFor="orbit-login-password">Password</label>
                <TextField
                  id="orbit-login-password"
                  placeholder="Enter your password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  fullWidth
                  error={Boolean(errors.password)}
                  helperText={errors.password?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockKeyhole size={21} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            aria-pressed={showPassword}
                          >
                            {showPassword ? (
                              <Eye size={20} />
                            ) : (
                              <EyeOff size={20} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...register("password")}
                />
              </div>
              <div className="orbit-login__options">
                <Controller
                  name="rememberMe"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                          inputRef={field.ref}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      }
                      label="Keep me signed in"
                    />
                  )}
                />
                <Button
                  component={RouterLink}
                  to="/reset-password"
                  variant="text"
                >
                  Forgot password?
                </Button>
              </div>
              <Button
                className="orbit-login__submit"
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting}
                endIcon={isSubmitting ? undefined : <ArrowRight size={22} />}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress
                      size={19}
                      color="inherit"
                      sx={{ mr: 1 }}
                    />
                    Signing in…
                  </>
                ) : (
                  "Log In"
                )}
              </Button>
            </Stack>
            {/* <div className="orbit-login__divider">
            <span>OR</span>
          </div>
          <div className="orbit-login__access">
            <span className="orbit-login__access-icon">
              <UsersRound size={23} />
            </span>
            <div>
              <strong>Need access?</strong>
              <p>Contact your administrator to get started.</p>
            </div>
          </div> */}
          </section>

          <aside className="orbit-login__story" aria-label="About ORBIT">
            <p className="orbit-login__eyebrow orbit-login__story-label">
              More than properties
            </p>
            <h2>
              Opportunity
              <br />
              <span>Lives Here</span>
            </h2>
            <p className="orbit-login__story-copy">
              A unified view of every property,
              <br className="orbit-login__desktop-break" /> lead and
              relationship. Built for
              <br className="orbit-login__desktop-break" /> a smarter real
              estate tomorrow.
            </p>
            <div className="orbit-login__features">
              <div>
                <span>
                  <House size={24} />
                </span>
                <strong>Properties</strong>
                <p>All in one place</p>
              </div>
              <div>
                <span>
                  <UsersRound size={24} />
                </span>
                <strong>Leads</strong>
                <p>
                  From inquiry
                  <br />
                  to opportunity
                </p>
              </div>
              <div>
                <span>
                  <ChartNoAxesColumnIncreasing size={24} />
                </span>
                <strong>Growth</strong>
                <p>
                  Driven by
                  <br />
                  insights
                </p>
              </div>
            </div>
          </aside>
        </div>

        <footer className="orbit-login__footer">
          <div className="orbit-login__developer">
            <img
              src="/branding/srmi-emblem.jpeg"
              alt="SRM International Real Estate LLC"
            />
            <div>
              <span>Developed by</span>
              <strong>SRM International Real Estate LLC</strong>
            </div>
          </div>
          <p>Lets Grow Together</p>
          <span className="orbit-login__closing">
            People
            <br />
            Properties
            <br />
            Possibilities
          </span>
        </footer>
      </main>
    </ThemeProvider>
  );
}
